import { useState, type FormEvent } from 'react';
import { api, type LaterWhen, type TodoItem } from '../../lib/api';
import ChatMarkdown from '../ChatMarkdown';
import { Button } from '../ui';
import LaterMenu from './LaterMenu';
import type { ClosedTodos } from './TodoList';
import { NO_CONTACT, dueDay, isDueByToday, sortByUrgency, untilLabel, wordsOf } from './todoFormat';

/** The reminder names at most this many (todoRules MAX_NAMED); the check-in says the rest as a count, the same way. */
const MAX_NAMED = 5;
const NOT_SAVED = 'Not saved - try again.';
const NO_ANSWER = "The assistant didn't answer - try again.";
/** ⭐ R34: the marker the assistant reads as "he is answering his check-in" - never a record on screen. */
const CHECK_IN_CONTEXT = 'todos';

interface Props {
  /** His open list as the page read it (`api.todos.list()`, the server's order). The opening lines are made from it HERE. */
  todos: TodoItem[];
  /** ⭐ R33: held by the page, so a Done here also shows on his list and its clean-up card - and the other way round. */
  closed: ClosedTodos;
  onClosed: (id: string, how: 'done' | 'dropped' | null) => void;
  /** The page re-reads his list: after a Later, and after every answer - the assistant may have changed a to-do. */
  onChanged: () => Promise<void>;
  onClose: () => void;
}

interface Exchange { you: string; reply: string }

/**
 * The reminder's set (pushService): due today or overdue by the Pacific date, and not put off with Later - a Later that
 * has ended is due again. ⭐ R50: in the reminder's own order (due today first, then the most recently overdue), so the
 * check-in names the five the push named.
 */
const dueNow = (todos: TodoItem[], now: Date): TodoItem[] => sortByUrgency(
  todos.filter((t) => isDueByToday(t.due_at, now) && !(t.snoozed_until && Date.parse(t.snoozed_until) > now.getTime())),
  now,
);

const stillOpen = (t: TodoItem): string =>
  `Still open: ${t.title}${t.contact_name ? ` (${t.contact_name})` : ''} - due ${dueDay(t.due_at as string)}`;

/**
 * TODO1 (spec 4.5) - the check-in, where a reminder lands (`/todos?checkin=1`) and what "Check in" opens. ⭐ Its opening
 * lines are written by CODE from his list, never by the model: what is due, with Done and Later on each. What he types
 * goes to the assistant, which can change his to-dos only on his words (R18) - so the page re-reads after every answer.
 * ⛔ Opening it writes nothing: every write is a tap, or his words sent.
 */
export default function CheckIn({ todos, closed, onClosed, onChanged, onClose }: Props) {
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  // What the last Later did, said back - its line leaves the check-in once the page has re-read.
  const [said, setSaid] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [answerError, setAnswerError] = useState<string | null>(null);
  const [chatId, setChatId] = useState<string | null>(null);
  const [exchanges, setExchanges] = useState<Exchange[]>([]);

  const due = dueNow(todos, new Date());
  const named = due.slice(0, MAX_NAMED);
  const more = due.length - named.length;

  const run = async (id: string, write: () => Promise<void>) => {
    setBusy((b) => ({ ...b, [id]: true }));
    setErrors(({ [id]: _gone, ...rest }) => rest);
    try {
      await write();
    } catch (e) {
      setErrors((er) => ({ ...er, [id]: wordsOf(e, NOT_SAVED) }));
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };
  // ⭐ Done and Undo do not re-read: the line keeps "Marked done" and its Undo until the next read, as on his list.
  const onDone = (t: TodoItem) => void run(t.ghl_task_id, async () => {
    await api.todos.done(t.ghl_task_id);
    onClosed(t.ghl_task_id, 'done');
  });
  const onUndo = (t: TodoItem) => void run(t.ghl_task_id, async () => {
    await api.todos.reopen(t.ghl_task_id);
    onClosed(t.ghl_task_id, null);
  });
  const onLater = (t: TodoItem, when: LaterWhen) => void run(t.ghl_task_id, async () => {
    const { until, title } = await api.todos.later(t.ghl_task_id, when);
    setSaid(`Hidden from reminders ${untilLabel(until)}: ${title}.`);
    await onChanged();
  });

  const send = async (e: FormEvent) => {
    e.preventDefault();
    const message = text.trim();
    if (!message || sending) return;
    setSending(true);
    setAnswerError(null);
    try {
      const answer = await api.assistantChat({ message, chat_id: chatId, page_context: CHECK_IN_CONTEXT });
      setChatId(answer.chat_id);
      setExchanges((x) => [...x, { you: message, reply: answer.message }]);
      setText('');
    } catch (err) {
      setAnswerError(wordsOf(err, NO_ANSWER));
    }
    // ⭐ After a failed answer too: a tool may have changed a to-do before the answer failed.
    await onChanged();
    setSending(false);
  };

  const line = (t: TodoItem) => {
    const id = t.ghl_task_id;
    const how = closed[id];
    if (how) {
      return (
        <li key={id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm text-gray-700">
          <p>{how === 'done' ? `Marked done: ${t.title}.` : `Dropped: ${t.title}.`}</p>
          <Button size="sm" variant="ghost" onClick={() => onUndo(t)} disabled={busy[id]}>Undo</Button>
          {errors[id] && <span role="alert" className="text-red-700">{errors[id]}</span>}
        </li>
      );
    }
    return (
      <li key={id} className="py-2">
        <p className="text-sm text-gray-900">{stillOpen(t)}</p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {t.ghl_contact_id !== null ? (
            <Button size="sm" onClick={() => onDone(t)} disabled={busy[id]}>Done</Button>
          ) : (
            <span className="text-xs text-gray-600">{NO_CONTACT}</span>
          )}
          <LaterMenu onPick={(when) => onLater(t, when)} disabled={busy[id]} />
        </div>
        {errors[id] && <p role="alert" className="mt-1 text-sm text-red-700">{errors[id]}</p>}
      </li>
    );
  };

  return (
    <section aria-label="Check-in" className="mb-6 rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <h2 className="text-base font-semibold text-gray-900">Check-in</h2>
        <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
      </div>

      {named.length > 0 ? (
        <ul aria-label="Due today" className="mt-2 divide-y divide-gray-100">{named.map(line)}</ul>
      ) : (
        <p className="mt-2 text-sm text-gray-700">Nothing due today.</p>
      )}
      {more > 0 && <p className="mt-1 text-sm text-gray-600">{`and ${more} more`}</p>}
      {said && <p role="status" className="mt-2 text-sm text-gray-700">{said}</p>}

      {exchanges.length > 0 && (
        <div className="mt-4 space-y-3">
          {exchanges.map((x, i) => (
            <div key={i} className="space-y-1 text-sm">
              {/* His words render literally; only the assistant's answer is markdown (ChatMarkdown's rule). */}
              <p className="whitespace-pre-wrap font-medium text-gray-900">{x.you}</p>
              <div className="rounded-lg bg-gray-50 px-3 py-2 text-gray-800"><ChatMarkdown text={x.reply} /></div>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={(e) => void send(e)} className="mt-4">
        <label htmlFor="todo-check-in-answer" className="block text-sm font-medium text-gray-700">Your answer</label>
        <textarea
          id="todo-check-in-answer"
          rows={2}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="What's done, what can wait - in your words."
          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
        />
        {answerError && <p role="alert" className="mt-1 text-sm text-red-700">{answerError}</p>}
        <div className="mt-2 flex justify-end">
          <Button type="submit" size="sm" disabled={!text.trim()} isLoading={sending}>Send</Button>
        </div>
      </form>
    </section>
  );
}
